import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Avatar } from '@/components/Avatar/Avatar';
import { Button } from '@/components/Button/Button';
import { Chip } from '@/components/Chip/Chip';
import { Popover } from '@/components/Popover/Popover';
import { StatusPill } from '@/components/StatusPill/StatusPill';
import { DescribeJobSheet } from '@/features/jobs';
import { t } from '@/lib/i18n';
import { formatPersonName } from '@/lib/format';
import type { Member } from '../schemas';
import type { MemberWorkingStatus } from '../utils';
import styles from './MemberCard.module.css';

export interface MemberCardProps {
  member: Member;
  status?: MemberWorkingStatus | null;
  jobsRunning?: number;
  canCreateJob?: boolean;
}

export function MemberCard({
  member,
  status = null,
  jobsRunning = 0,
  canCreateJob = false,
}: MemberCardProps) {
  const [assignOpen, setAssignOpen] = useState(false);
  const [describeOpen, setDescribeOpen] = useState(false);

  const primaryPosition = member.positions[0];
  const isAi = member.kind === 'ai';
  const isArchived = Boolean(member.archivedAt);
  const prefillMember = { id: member.id, name: formatPersonName(member) };
  const editUrl = `/team/m/${encodeURIComponent(member.id)}/edit`;

  return (
    <div className={`${styles.wrapper}${status === 'blocked' ? ` ${styles.wrapperBlocked}` : ''}`}>
      <Link
        to={`/team/m/${encodeURIComponent(member.id)}`}
        className={styles.link}
        aria-label={`${formatPersonName(member)} — ${primaryPosition?.name ?? t('No position assigned')}`}
      >
        <Avatar name={formatPersonName(member)} size="lg" kind={member.kind} />
        <div className={styles.body}>
          <div className={styles.header}>
            <span className={styles.name}>{formatPersonName(member)}</span>
            {member.kind === 'ai' ? (
              <Chip variant="ai" size="sm">
                {t('AI')}
              </Chip>
            ) : null}
          </div>
          {primaryPosition ? <div className={styles.subtitle}>{primaryPosition.name}</div> : null}
          {member.units.length > 0 ? (
            <div className={styles.units}>
              {member.units.map((u) => (
                <Chip key={u.id} variant="neutral" size="sm">
                  {u.name}
                </Chip>
              ))}
            </div>
          ) : null}
        </div>
      </Link>

      {!primaryPosition || member.units.length === 0 ? (
        <div className={styles.assignSection}>
          {!primaryPosition ? (
            <Link to={editUrl} className={styles.assignLink}>
              {t('Assign position')}
            </Link>
          ) : null}
          {member.units.length === 0 ? (
            <Link to={editUrl} className={styles.assignLink}>
              {t('Assign department')}
            </Link>
          ) : null}
        </div>
      ) : null}

      {!isArchived ? (
        <div
          className={`${styles.footer}${status === 'blocked' ? ` ${styles.footerBlocked}` : ''}`}
        >
          {!isAi ? <StatusPill variant="muted">{t('Idle')}</StatusPill> : null}
          {isAi && status === 'working' ? (
            <StatusPill variant="success" dot>
              {t('Working · {n} {job}', {
                n: jobsRunning,
                job: jobsRunning === 1 ? t('job') : t('jobs'),
              })}
            </StatusPill>
          ) : null}
          {isAi && status === 'idle' ? <StatusPill variant="muted">{t('Idle')}</StatusPill> : null}
          {isAi && status === 'blocked' ? (
            <StatusPill variant="danger">{t('Blocked')}</StatusPill>
          ) : null}

          <div className={styles.footerActions}>
            {isAi ? (
              <Link to={`/team/m/${encodeURIComponent(member.id)}/chat`}>
                <Button variant="secondary" size="sm">
                  {t('Chat')}
                </Button>
              </Link>
            ) : null}
            {isAi && canCreateJob ? (
              <>
                <div className={styles.footerDivider} aria-hidden="true" />
                <Popover
                  open={assignOpen}
                  onOpenChange={setAssignOpen}
                  align="end"
                  trigger={
                    <Button variant="secondary" size="sm" aria-haspopup="menu">
                      {t('Assign work')} <span aria-hidden="true">{'▾'}</span>
                    </Button>
                  }
                >
                  <div role="menu" className={styles.popoverMenu}>
                    <button
                      type="button"
                      role="menuitem"
                      className={styles.popoverMenuItem}
                      onClick={() => {
                        setAssignOpen(false);
                        setDescribeOpen(true);
                      }}
                    >
                      <div>
                        <div>{t('Describe a Job')}</div>
                        <div className={styles.popoverMenuItemDesc}>
                          {t('Tell {name} what you need in plain English', {
                            name: member.first_name,
                          })}
                        </div>
                      </div>
                    </button>
                    <div className={styles.popoverMenuSep} aria-hidden="true" />
                    <Link
                      to={`/jobs/new?member=${encodeURIComponent(member.id)}`}
                      role="menuitem"
                      className={styles.popoverMenuItem}
                      onClick={() => setAssignOpen(false)}
                    >
                      <div>
                        <div>{t('Pick from library')}</div>
                        <div className={styles.popoverMenuItemDesc}>
                          {t('Choose a saved Job Template')}
                        </div>
                      </div>
                    </Link>
                  </div>
                </Popover>
              </>
            ) : null}
          </div>
        </div>
      ) : null}

      {describeOpen ? (
        <DescribeJobSheet open onOpenChange={setDescribeOpen} prefillMember={prefillMember} />
      ) : null}
    </div>
  );
}
